import { useMemo } from 'react';
import { Alert, SimpleGrid, Stack } from '@mantine/core';
import type { ComponentProjectMetricsResponse } from '../../../../types/dashboard';
import { ComponentProjectMetricChartCard } from '../ComponentProjectMetricChartCard';
import { AswpInsightsCard } from './AswpInsightsCard';
import { AswpProgramParticipationCard } from './AswpProgramParticipationCard';
import {
  hasAswpInsightsData,
  hasAswpProgramParticipationData,
  isAswpInsightsMetric,
  isAswpProgramMetric,
  mapAswpComponentProjectData,
} from './mapAswpComponentProjectData';

type AswpComponentProjectMetricsProps = {
  data: ComponentProjectMetricsResponse;
};

export function AswpComponentProjectMetrics({ data }: AswpComponentProjectMetricsProps) {
  const aswpData = useMemo(() => mapAswpComponentProjectData(data), [data]);

  const otherMetrics = useMemo(() => {
    if (!data?.sections?.length) return [];
    return data.sections.flatMap((section) =>
      section.metrics
        .filter(
          (metric) =>
            !isAswpProgramMetric(metric.metricId, metric) &&
            !isAswpInsightsMetric(metric.metricId, metric)
        )
        .map((metric) => ({
          key: `${section.sectionId}-${metric.metricId}`,
          metric,
          sectionTitle: section.title,
        }))
    );
  }, [data]);

  const showProgramParticipation =
    aswpData !== null && hasAswpProgramParticipationData(aswpData);
  const showInsights = aswpData !== null && hasAswpInsightsData(aswpData);

  if (!showProgramParticipation && !showInsights && otherMetrics.length === 0) {
    return (
      <Alert color="blue" variant="light">
        No ASWP component project metrics are available yet.
      </Alert>
    );
  }

  return (
    <Stack spacing="lg">
      {showProgramParticipation && aswpData ? (
        <AswpProgramParticipationCard data={aswpData} />
      ) : null}
      {showInsights && aswpData?.insights ? (
        <SimpleGrid cols={2} spacing="lg" breakpoints={[{ maxWidth: 1200, cols: 1 }]}>
          <AswpInsightsCard data={aswpData.insights} />
        </SimpleGrid>
      ) : null}
      {otherMetrics.length > 0 ? (
        <SimpleGrid
          cols={2}
          spacing="lg"
          breakpoints={[{ maxWidth: 1200, cols: 1 }]}
          sx={{ alignItems: 'stretch' }}
        >
          {otherMetrics.map(({ key, metric, sectionTitle }) => (
            <ComponentProjectMetricChartCard
              key={key}
              metric={metric}
              sectionTitle={sectionTitle}
            />
          ))}
        </SimpleGrid>
      ) : null}
    </Stack>
  );
}
